import Button from '@/common/components/Button'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import { cn } from '@/common/utils/cn'
import { formatPercentage } from '@/common/utils/formatters'
import { IntegrationCodeMatch } from '@/features/modelIntegrationCodes/types/suggestedModel.type'
import { getSimilarityColor } from '@/features/modelIntegrationCodes/utils/getSimilarityColor'
import { ImageOffIcon, Info } from 'lucide-react'

interface SelectedModelProps {
  selectedModel?: IntegrationCodeMatch
  onConfirm: () => void
  isConfirming?: boolean
}

const SelectedModel = ({ selectedModel, onConfirm, isConfirming }: SelectedModelProps) => {
  if (!selectedModel) {
    return (
      <div className="flex-1 bg-yellow-op-2 p-4 rounded-sm flex flex-col gap-2">
        <p className="text-sm font-bold">Modelo selecionado</p>
        <div className="flex items-center gap-1 text-[10px] leading-3">
          <Info size={24} className="shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <p className="font-bold">Selecione um modelo na esquerda para comparar semelhanças</p>
            <p className="font-light italic">Encontre nos mais semelhantes ou realize uma busca geral</p>
          </div>
        </div>
      </div>
    )
  }

  const colorClass = getSimilarityColor(selectedModel.score)
  const label = selectedModel.name?.trim() || selectedModel.integration_code_value
  const rankEntries = Object.entries(selectedModel.rank_fields ?? {})

  return (
    <div className="flex-1 bg-white-100 p-4 rounded-sm flex flex-col gap-2 min-h-0">
      <p className="text-sm font-bold">Modelo selecionado</p>

      <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-auto pr-2">
        <div className="flex gap-2 items-center w-full">
          <ImageWithPlaceholder
            className="h-16 w-auto aspect-video rounded-sm bg-white-400"
            url={selectedModel.image_url}
            placeholderIcon={ImageOffIcon}
            iconClassName="size-8"
            placeholderIconProps={{ strokeWidth: 2 }}
          />

          <div className="flex-1 min-w-0 bg-white-200 p-2 rounded-sm flex flex-col gap-1">
            <p className="text-[10px]">Nome do modelo</p>
            <p className="text-xs font-bold">{label}</p>
          </div>

          <div className="flex flex-col items-end shrink-0">
            <p className={cn('text-[10px] italic font-light leading-3', colorClass)}>Score Total</p>
            <p className={cn('text-xs font-bold leading-3', colorClass)}>
              {!!selectedModel.score && selectedModel.score >= 0 ? formatPercentage(selectedModel.score) : '--'}
            </p>
          </div>
        </div>

        {rankEntries.map(([fieldLabel, rank]) => {
          const rowColor = getSimilarityColor(rank.score)
          return (
            <div key={fieldLabel} className="flex flex-col gap-1">
              <p className="text-[10px] italic font-light leading-3">{fieldLabel}</p>

              <div className="flex items-stretch gap-1 text-xs">
                <div
                  className={cn(
                    'w-20 shrink-0 bg-white-200 rounded-sm flex items-center justify-center font-bold px-1 text-center',
                    rowColor,
                  )}
                >
                  {!!rank.score && rank.score >= 0 ? formatPercentage(rank.score) : '--'}
                </div>

                <div className="flex-1 min-w-0 bg-white-200 p-2 rounded-sm flex flex-col gap-0.5">
                  <p className="text-[10px] italic font-light leading-3">Referência</p>
                  <p className="font-bold">{rank.from ? String(rank.from) : '--'}</p>
                </div>

                <div className="flex-1 min-w-0 bg-white-200 p-2 rounded-sm flex flex-col gap-0.5">
                  <p className="text-[10px] italic font-light leading-3">Integração</p>
                  <p className="font-bold">{rank.to ? String(rank.to) : '--'}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex justify-end">
        <Button
          name="confirm_homologation"
          isLoading={isConfirming}
          onClick={onConfirm}
          operationKlassToCheckPermission="Admin::EquipmentModels::LinkIntegrationCode"
        >
          Confirmar e homologar
        </Button>
      </div>
    </div>
  )
}

export default SelectedModel
